import { useEffect, useState } from "preact/hooks";
import { getLang, locale, tr } from "../i18n.js";
import { get, html, IN_APP, post } from "../lib.js";
import { AI_KEY_BLOCK_TEXT, KEY_PROBLEM_TEXT } from "./agent.js";
import { Badge, ErrorBox, Field, Loading, Modal, toast, useAction, useForm, useLoad } from "../ui.js";

const ils = (n) => {
  const digits = Number.isInteger(Number(n)) ? 0 : 2;
  return `₪${Number(n).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: 2 })}`;
};
const agorot = (n) => ils(n / 100);
const fmtDate = (iso) => (iso ? new Intl.DateTimeFormat(locale(), { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso)) : "—");
const daysLeft = (iso) => Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000));

/** The binding terms of use on the marketing site, in the viewer's language. */
export const termsHref = () => (getLang() === "he" ? "https://relayza.company/he/terms" : "https://relayza.company/terms");

/** "I accept the terms of use" with a link to them; the card-clearing rules require it on the payment step. */
export function TermsCheck({ checked, onChange, adult = false }) {
  return html`<label class="toggle terms-check"><input type="checkbox" required checked=${checked} onChange=${(e) => onChange(e.target.checked)} />
    <span>${adult ? tr("I confirm I am 18 or older, and I have read and accept the ") : tr("I have read and accept the ")}<a href=${termsHref()} target="_blank" rel="noopener">${tr("terms of use")}</a></span></label>`;
}

export const STATUS_BADGE = {
  pending: ["warn", "Awaiting payment"],
  trialing: ["accent", "Trial"],
  active: ["ok", "Active"],
  past_due: ["warn", "Payment failed"],
  unpaid: ["warn", "Unpaid"],
  expired: ["warn", "Trial ended"],
  cancelled: ["neutral", "Cancelled"],
  comped: ["ok", "Complimentary"],
};

/** One-line account notice shown above every page when billing needs attention. */
export function BillingBanner({ t }) {
  const b = t.billing;
  if (IN_APP || !b || !["owner", "admin"].includes(t.role) || location.pathname === "/settings/billing") return null;
  let kind = "warn";
  let msg = null;
  if (b.card_update_due_at && ["active", "past_due"].includes(b.status)) {
    msg = tr("Please add your payment card again by {date} so your plan renews without interruption.", { date: fmtDate(b.card_update_due_at) });
    return html`<div class="callout warn billing-banner"><span>${msg}</span> <a href="/settings/billing">${tr("Update card")} →</a></div>`;
  }
  if (b.status === "pending") {
    return html`<div class="callout warn billing-banner"><span>${tr("Finish your payment to switch on the AI agent.")}</span> <a href="/settings/billing">${tr("Complete payment")} →</a></div>`;
  }
  // No Anthropic key can pay for a call: the agent is off until the owner adds one (or moves up a
  // plan). Only the two reasons a key fixes; a dead subscription is covered by the branches below.
  if (["trial_credit_spent", "own_key_required"].includes(t.ai_key?.block)) {
    return html`<div class="callout warn billing-banner"><span>${tr(AI_KEY_BLOCK_TEXT[t.ai_key.block])}</span> <a href="/agent">${tr("Add your key")} →</a></div>`;
  }
  // The business's own key stopped working (revoked, or the Anthropic balance ran out).
  if (t.ai_key?.own_key?.problem) {
    return html`<div class="callout warn billing-banner"><span>${tr(KEY_PROBLEM_TEXT[t.ai_key.own_key.problem])}</span> <a href="/agent">${tr("Check your key")} →</a></div>`;
  }
  // Most of the trial's AI credit is gone: the one moment to add a key before chats go to a person.
  const credit = t.ai_key?.trial_credit;
  if (credit && !t.ai_key.block && credit.spent >= credit.cap * 0.8) {
    return html`<div class="callout warn billing-banner"><span>${tr("You've used {pct}% of the trial's AI credit. Add your Anthropic API key, or start a plan that includes AI usage, so the agent keeps answering when it runs out.", { pct: Math.min(100, Math.round((credit.spent / credit.cap) * 100)) })}</span> <a href="/agent">${tr("Add your key")} →</a></div>`;
  }
  if (b.status === "trialing") {
    const d = daysLeft(b.trial_ends_at);
    if (d > 3 && b.ai_block !== "limit") return null;
    kind = d > 1 ? "" : "warn";
    msg =
      b.ai_block === "limit"
        ? tr("Your trial's {n} AI conversations are used up; new chats go to your team.", { n: b.conversations_included })
        : d === 1
          ? tr("Your free trial ends tomorrow.")
          : tr("Your free trial ends in {n} days.", { n: d });
  } else if (b.status === "expired") msg = tr("Your free trial has ended. The AI agent is paused; new chats go to your team.");
  else if (b.status === "past_due") msg = tr("We couldn't charge your card. We'll retry automatically, or update your payment now.");
  else if (b.status === "unpaid") msg = tr("Your subscription is unpaid. The AI agent is paused until the balance is paid.");
  else if (b.status === "cancelled") msg = tr("Your subscription has ended. The AI agent is paused.");
  else if (b.ai_block === "limit") msg = tr("You've hit this month's safety limit for AI conversations; new chats go to your team.");
  else if (b.conversations_included && b.conversations_used >= b.conversations_included) {
    msg = tr("You've used all {n} included AI conversations this month; extra ones are billed at ₪0.60.", { n: b.conversations_included });
  }
  if (!msg) return null;
  return html`<div class="callout ${kind} billing-banner"><span>${msg}</span> <a href="/settings/billing">${b.status === "trialing" || b.status === "expired" ? tr("Choose a plan") : tr("Billing")} →</a></div>`;
}

export function Billing({ t }) {
  const [data, reload, error] = useLoad(() => get(`${t.base}/billing`), [t.id]);
  const [interval, setBillingInterval] = useState("month");
  const [choosing, setChoosing] = useState(null);
  const [busy, run] = useAction();

  useEffect(() => {
    const status = new URLSearchParams(location.search).get("payment");
    if (status === "success") toast(tr("Payment received. Thank you!"));
    if (status === "card") toast(tr("Your card was saved"));
    if (status === "failed") toast(tr("The payment didn't go through"), "error");
    if (status) {
      history.replaceState(null, "", location.pathname);
      // The provider's notification can land a moment after the redirect.
      setTimeout(() => {
        reload();
        t.reload();
      }, 2500);
    }
  }, []);

  if (error) return html`<${ErrorBox} error=${error} />`;
  if (!data) return html`<${Loading} />`;
  const isOwner = t.role === "owner";
  const paying = ["active", "past_due"].includes(data.status) && data.has_card;
  const needsCard = ["active", "past_due"].includes(data.status) && !data.has_card;
  const [badgeKind, badgeLabel] = STATUS_BADGE[data.status] ?? ["neutral", data.status];
  const vatPct = Math.round(data.catalog.vat_rate * 100);
  const after = async () => {
    reload();
    t.reload();
  };

  const pay = async () => {
    const { url } = await run(() => post(`${t.base}/billing/pay`));
    location.href = url;
  };
  const updateCard = async () => {
    const { url } = await run(() => post(`${t.base}/billing/card`));
    location.href = url;
  };
  const cancel = async () => {
    if (!confirm(tr("Cancel at the end of the current period? The AI agent stops when it ends."))) return;
    await run(() => post(`${t.base}/billing/cancel`), tr("Your plan will end at the close of this period"));
    after();
  };
  const resume = async () => {
    await run(() => post(`${t.base}/billing/resume`), tr("Your plan will renew as usual"));
    after();
  };

  return html`<div>
    <div class="grid-2">
      <section class="card">
        <div class="section-head"><h2>${tr("{plan} plan", { plan: data.plan_name })}</h2><${Badge} kind=${badgeKind}>${tr(badgeLabel)}</${Badge}></div>
        <${PlanStatus} data=${data} />
        <${UsageMeter} data=${data} />
        ${data.outstanding_agorot > 0 && html`<div class="callout warn small">${tr("Outstanding balance: {amount} incl. VAT.", { amount: agorot(data.outstanding_agorot) })}
          ${isOwner && html` <button class="btn small" disabled=${busy || !data.payments_enabled} onClick=${pay}>${tr("Pay now")}</button>`}</div>`}
        ${paying && isOwner && html`<div class="form-actions">
          ${data.cancel_at_period_end
            ? html`<button class="btn" disabled=${busy} onClick=${resume}>${tr("Keep my plan")}</button>`
            : html`<button class="btn ghost danger" disabled=${busy} onClick=${cancel}>${tr("Cancel plan")}</button>`}
        </div>`}
      </section>
      <section class="card">
        <h2>${tr("Payment method")}</h2>
        ${data.card_update_due_at && html`<div class="callout warn small">${tr("We moved to a new payment account, so saved cards need to be added again. Please update your card by {date}.", { date: fmtDate(data.card_update_due_at) })}</div>`}
        ${data.has_card
          ? html`<p>${tr("Card ending {last4}, charged automatically each period.", { last4: data.card_last4 ?? "••••" })}</p>
              <p class="muted small">${tr("Tax invoice-receipts are issued by Morning and emailed to {email}.", { email: data.billing_details.email || tr("the owner") })}</p>`
          : needsCard
            ? html`<p class="muted">${tr("No card on file. Add one so your plan renews.")}</p>`
            : html`<p class="muted">${tr("No card on file. You'll add one on the secure payment page when you choose a plan.")}</p>`}
        ${isOwner && data.payments_enabled && ["active", "past_due"].includes(data.status) && html`<div class="form-actions">
          <button class="btn ${data.card_update_due_at ? "primary" : ""}" disabled=${busy} onClick=${updateCard}>${data.has_card ? tr("Replace card") : tr("Update card")}</button>
        </div>
        ${data.card_check_agorot > 0 && html`<p class="muted small">${tr("A {amount} card check is charged and credited on your next bill.", { amount: agorot(data.card_check_agorot) })}</p>`}`}
        ${data.credit_agorot > 0 && html`<p class="muted small">${tr("Credit for your next bill: {amount}.", { amount: agorot(data.credit_agorot) })}</p>`}
        ${!data.payments_enabled && html`<div class="callout small">${tr("Online payment isn't switched on yet.")} <a href="mailto:hello@relayza.company?subject=Relayza%20subscription">${tr("Contact us to subscribe.")}</a></div>`}
      </section>
    </div>

    ${data.status !== "comped" && html`<section class="card">
      <div class="section-head">
        <h2>${paying ? tr("Change plan") : tr("Choose a plan")}</h2>
        <div class="segmented" role="group" aria-label=${tr("Billing period")}>
          <button class=${interval === "month" ? "active" : ""} onClick=${() => setBillingInterval("month")}>${tr("Monthly")}</button>
          <button class=${interval === "year" ? "active" : ""} onClick=${() => setBillingInterval("year")}>${tr("Yearly · 2 months free")}</button>
        </div>
      </div>
      <div class="plan-grid">
        ${data.catalog.plans.map((p) => {
          const current = paying && p.id === data.plan && interval === data.interval;
          return html`<div class="plan-card ${p.id === "growth" ? "featured" : ""}">
            <div class="section-head"><h3>${p.name}</h3>${p.id === "growth" && html`<${Badge} kind="accent">${tr("Most popular")}</${Badge}>`}</div>
            <div class="plan-price">${ils(interval === "year" ? p.annual_ils : p.monthly_ils)}<span class="muted small"> ${interval === "year" ? tr("/ year + VAT") : tr("/ month + VAT")}</span></div>
            <ul class="plain small plan-features">
              <li>${tr("{n} AI conversations a month", { n: p.conversations.toLocaleString() })}</li>
              <li>${p.channel_types.length === 1 ? tr("WhatsApp + website chat ({n} connected)", { n: p.channels }) : tr("WhatsApp, Instagram, Messenger + website chat ({n} connected)", { n: p.channels })}</li>
              <li>${p.seats ? tr("{n} team members", { n: p.seats }) : tr("Unlimited team members")}</li>
              <li>${p.resources ? tr("{n} staff or rooms", { n: p.resources }) : tr("Unlimited staff and rooms")}</li>
              <li>${p.followups ? tr("Automatic follow-ups") : html`<span class="muted">${tr("No automatic follow-ups")}</span>`}</li>
              <li>${p.webhooks ? tr("CRM webhooks") : html`<span class="muted">${tr("No CRM webhooks")}</span>`}</li>
              <li>${p.ai_included ? tr("Claude AI usage included") : html`<span class="muted">${tr("Bring your own Claude API key")}</span>`}</li>
              <li class="muted">${tr("Extra conversations {price} each", { price: agorot(p.overage_agorot) })}</li>
              ${p.id === "pro" && html`<li>${tr("Choice of the most capable AI model")}</li>`}
            </ul>
            ${needsCard
              ? html`<p class="muted small">${tr("Update your card first to change plans.")}</p>`
              : isOwner
              ? html`<button class="btn full ${p.id === "growth" ? "primary" : ""}" disabled=${busy || current || !data.payments_enabled} onClick=${() => setChoosing(p)}>
                  ${current ? tr("Current plan") : paying ? tr("Switch to {plan}", { plan: p.name }) : tr("Choose {plan}", { plan: p.name })}</button>`
              : html`<p class="muted small">${tr("Only the owner can change the plan.")}</p>`}
          </div>`;
        })}
      </div>
      <p class="muted small">${data.catalog.plans.every((p) => p.ai_included)
        ? tr("Extra AI conversations beyond your plan: {price} each. Prices exclude {vat}% VAT.", { price: agorot(data.catalog.overage_agorot), vat: vatPct })
        : tr("Extra AI conversations beyond your plan: {price} each, or {byo} on the plans where you bring your own Claude key, since Anthropic already bills you for those conversations. Prices exclude {vat}% VAT.", { price: agorot(data.catalog.overage_agorot), byo: agorot(data.catalog.byo_overage_agorot), vat: vatPct })}
        ${data.setup_fee_due ? ` ${tr("A one-time {fee} setup and onboarding fee applies to the first payment.", { fee: ils(data.catalog.setup_fee_ils) })}` : ""}
        ${" "}${tr("WhatsApp conversation fees are billed by Meta directly.")}</p>
    </section>`}

    ${choosing && html`<${ConfirmPlan} t=${t} data=${data} plan=${choosing} interval=${interval} paying=${paying} onClose=${() => setChoosing(null)} onDone=${after} />`}

    <section class="card table-wrap">
      <h2>${tr("Invoices and charges")}</h2>
      ${data.charges.length
        ? html`<table class="table">
            <thead><tr><th>${tr("Date")}</th><th>${tr("Description")}</th><th>${tr("Total")}</th><th>${tr("Status")}</th><th></th></tr></thead>
            <tbody>${data.charges.map((c) => html`<tr>
              <td class="small">${fmtDate(c.paid_at ?? c.created_at)}</td>
              <td class="small" dir="auto">${c.lines.map((l) => html`<div>${l.description}</div>`)}</td>
              <td>${agorot(c.total_agorot)}</td>
              <td>${chargeBadge(c)}</td>
              <td class="row-actions">${c.document_url && html`<a class="btn small ghost" href=${c.document_url} target="_blank" rel="noopener">${tr("Invoice")}</a>`}</td>
            </tr>`)}</tbody>
          </table>`
        : html`<p class="muted small">${tr("No charges yet.")}</p>`}
    </section>
  </div>`;
}

function chargeBadge(c) {
  if (c.status === "paid") return html`<${Badge} kind="ok">${tr("Paid")}</${Badge}>`;
  if (c.status === "void") return html`<${Badge} kind="muted">${tr("Not completed")}</${Badge}>`;
  if (c.status === "failed") return html`<${Badge} kind="warn">${tr("Failed")}</${Badge}>`;
  return html`<span title=${c.last_error ?? ""}><${Badge} kind="warn">${c.attempts ? tr("Retrying {date}", { date: fmtDate(c.next_attempt_at) }) : tr("Due")}</${Badge}></span>`;
}

function PlanStatus({ data }) {
  if (data.status === "pending") return html`<p>${tr("Your account is ready. Choose your plan below and complete the payment to switch on the AI agent.")}</p>`;
  if (data.status === "trialing") {
    return html`<p>${tr("Free trial until {date} ({n} days left). No card needed until you choose a plan.", { date: fmtDate(data.trial_ends_at), n: daysLeft(data.trial_ends_at) })}</p>`;
  }
  if (data.status === "expired") return html`<p>${tr("Your trial ended on {date}. Choose a plan to switch the AI agent back on.", { date: fmtDate(data.trial_ends_at) })}</p>`;
  if (data.status === "comped") return html`<p>${tr("Complimentary early-access plan. Thank you for being one of our first businesses.")}</p>`;
  if (data.status === "cancelled") return html`<p>${tr("This subscription has ended. Choose a plan to start again.")}</p>`;
  if (data.status === "unpaid") return html`<p>${tr("Payment failed several times, so the AI agent is paused. Pay the balance to switch it back on.")}</p>`;
  const date = fmtDate(data.current_period_end);
  return html`<p>
    ${data.cancel_at_period_end
      ? tr("Ends on {date}.", { date })
      : data.interval === "year"
        ? tr("Renews yearly on {date}.", { date })
        : tr("Renews monthly on {date}.", { date })}
    ${data.pending_plan &&
    ` ${data.pending_interval === "year"
      ? tr("Switches to {plan} (yearly) at renewal.", { plan: data.pending_plan })
      : tr("Switches to {plan} (monthly) at renewal.", { plan: data.pending_plan })}`}
  </p>`;
}

function UsageMeter({ data }) {
  const used = data.conversations_used;
  const included = data.conversations_included;
  const pct = included ? Math.min(100, Math.round((used / included) * 100)) : 0;
  return html`<div class="usage">
    <div class="section-head small"><span>${data.status === "trialing" ? tr("AI conversations this trial") : tr("AI conversations this month")}</span>
      <strong dir="ltr">${used.toLocaleString()}${included ? ` / ${included.toLocaleString()}` : ""}</strong></div>
    ${included && html`<div class="meter" role="progressbar" aria-valuenow=${pct} aria-valuemin="0" aria-valuemax="100"><div class=${pct >= 100 ? "over" : pct >= 80 ? "near" : ""} style=${`width:${pct}%`}></div></div>`}
    <p class="muted small">${data.status === "trialing" ? "" : `${tr("Resets {date}.", { date: fmtDate(data.usage_period_end) })} `}${tr("A conversation is one customer chat the AI handled in the period, however many messages it takes.")}</p>
  </div>`;
}

function ConfirmPlan({ t, data, plan, interval, paying, onClose, onDone }) {
  const d = data.billing_details;
  const { values, bind } = useForm({ name: d.name ?? t.name, email: d.email ?? "", tax_id: d.tax_id ?? "" });
  const [accepted, setAccepted] = useState(false);
  const [busy, run] = useAction();
  const price = interval === "year" ? plan.annual_ils : plan.monthly_ils;
  const fee = data.setup_fee_due && !paying ? data.catalog.setup_fee_ils : 0;
  const subtotal = price + fee;
  const vat = Math.round(subtotal * data.catalog.vat_rate * 100) / 100;
  const yearly = interval === "year";

  const submit = async (e) => {
    e.preventDefault();
    const billing = { name: values.name, tax_id: values.tax_id.trim(), ...(values.email ? { email: values.email } : {}) };
    const res = await run(() => post(`${t.base}/billing/subscribe`, { plan: plan.id, interval, billing, accept_terms: true }));
    if (res.url) {
      location.href = res.url;
      return;
    }
    toast(res.effective === "now" ? tr("You're on {plan} now", { plan: plan.name }) : tr("{plan} starts at your next renewal", { plan: plan.name }));
    onClose();
    onDone();
  };

  return html`<${Modal} title=${yearly ? tr("{plan}, yearly", { plan: plan.name }) : tr("{plan}, monthly", { plan: plan.name })} onClose=${onClose}>
    <form onSubmit=${submit}>
      ${paying
        ? html`<p class="small">${tr("Upgrades start now; we charge your saved card for the difference for the rest of this period. Downgrades and billing-period changes start at your next renewal.")}</p>`
        : html`<table class="table small"><tbody>
            <tr><td>${yearly ? tr("{plan}, first year", { plan: plan.name }) : tr("{plan}, first month", { plan: plan.name })}</td><td>${ils(price)}</td></tr>
            ${fee > 0 && html`<tr><td>${tr("Setup and onboarding (one time)")}</td><td>${ils(fee)}</td></tr>`}
            <tr><td>${tr("VAT {vat}%", { vat: Math.round(data.catalog.vat_rate * 100) })}</td><td>${ils(vat)}</td></tr>
            <tr><td><strong>${tr("Total today")}</strong></td><td><strong>${ils(subtotal + vat)}</strong></td></tr>
          </tbody></table>
          <p class="muted small">${yearly
            ? tr("Your card is saved securely by our payment provider and charged automatically each year. Cancel any time; the plan runs to the end of the paid period.")
            : tr("Your card is saved securely by our payment provider and charged automatically each month. Cancel any time; the plan runs to the end of the paid period.")}</p>`}
      <h3>${tr("Invoice details")}</h3>
      <${Field} label=${tr("Business name on invoice")}><input required ...${bind("name")} /></${Field}>
      <div class="row">
        <${Field} label=${html`${tr("Business number")} (<bdi dir="rtl">ח.פ. / ע.מ.</bdi>)`} hint=${tr("Needed for VAT-deductible invoices")}><input inputmode="numeric" pattern="\\d{8,9}" ...${bind("tax_id")} /></${Field}>
        <${Field} label=${tr("Invoice email")} hint=${tr("Defaults to the owner's email")}><input type="email" ...${bind("email")} /></${Field}>
      </div>
      <${TermsCheck} checked=${accepted} onChange=${setAccepted} />
      <div class="form-actions"><button class="btn ghost" type="button" onClick=${onClose}>${tr("Back")}</button>
        <button class="btn primary" disabled=${busy || !accepted}>${paying ? tr("Confirm change") : tr("Continue to secure payment")}</button></div>
    </form>
  </${Modal}>`;
}
